// SupplierDetailPanel.jsx — supplier management surface (spec §6, Suppliers page).
// Fetches the master + aliases (provenance) + templates via getsupplieraliases.sz.
// Actions, all backed by the Phase 1 primitives / sm_validated through .sz endpoints:
//   header "Mark as payee" / "Remove payee mark" → setsupplierpayee.sz
//   per-alias "Move…"  → relinksupplieralias.sz
//   footer "Merge into another supplier…" → mergesuppliermasters.sz
import { useEffect, useState } from "react";
import { Offcanvas, Badge, Button } from "react-bootstrap";
import { format, parseISO } from "date-fns";
import useFetchData from "./useFetchDatav2";
import { useToast } from "../Context/useToast";
import PayeePickerModal from "./PayeePickerModal";

const SOURCE_VARIANT = {
   PROTRACTOR: "primary",
   USER_CONFIRMED: "success",
   OCR_AUTO: "warning",
   SYSTEM: "secondary",
   ONBOARDING: "info",
};

const fmtDate = (d) => {
   if (!d) return null;
   try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; }
};

// Protractor reports operators as "Pro_AB2163\Cory Castillo" — the domain half is noise.
const protractorUser = (s) => (s && s.includes("\\") ? s.split("\\").pop() : s);

const addressLine = (rec) => {
   const cityLine = [rec?.city, rec?.province, rec?.postal_code].filter(Boolean).join(", ");
   return [rec?.street, cityLine].filter(Boolean).join(" · ");
};

export default function SupplierDetailPanel({ supplier, onClose, onChanged }) {
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();
   const [detail, setDetail] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [pickerMode, setPickerMode] = useState(null);   // null | {type:'move', alias} | {type:'merge'}
   const [pendingAction, setPendingAction] = useState(null); // {mode, target}
   const [isWorking, setIsWorking] = useState(false);

   const loadDetail = async () => {
      if (!supplier?.supplierId) return;
      setIsLoading(true);
      try {
         const formData = new FormData();
         formData.append("_supplier_ext_id", supplier.supplierId);
         const { responseData } = await fetchData("/getsupplieraliases.sz", "POST", formData, "form-data");
         setDetail(responseData?.st === 1 && responseData?.payload?.length > 0 ? responseData.payload[0] : null);
      } catch {
         setDetail(null);
      } finally {
         setIsLoading(false);
      }
   };

   useEffect(() => {
      loadDetail();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [supplier?.supplierId]);

   const setPayeeMark = async (make) => {
      setIsWorking(true);
      try {
         const formData = new FormData();
         formData.append("_supplier_ext_id", supplier.supplierId);
         formData.append("_make", make ? "1" : "0");
         const { responseData } = await fetchData("/setsupplierpayee.sz", "POST", formData, "form-data");
         const result = responseData?.payload?.[0];
         if (responseData?.st === 1 && result?.status === "ok") {
            triggerToast("success",
               make
                  ? `${supplier.name} is now a payee.`
                  : `Payee mark removed from ${supplier.name}.`,
               false, make ? "Marked as payee" : "Payee mark removed", false);
            await loadDetail();
            onChanged?.();
         } else {
            triggerToast("warning", result?.reason || responseData?.mg || "Action failed.", false, "", false);
         }
      } catch {
         triggerToast("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
      } finally {
         setIsWorking(false);
      }
   };

   const runAction = async () => {
      const { mode, target } = pendingAction || {};
      if (!mode || !target?.id) return;
      setIsWorking(true);
      try {
         const formData = new FormData();
         let url;
         if (mode.type === "move") {
            url = "/relinksupplieralias.sz";
            formData.append("_alias_id", mode.alias.alias_id);
            formData.append("_master_id", target.id);
            formData.append("_surface", "suppliers_page");
         } else {
            url = "/mergesuppliermasters.sz";
            formData.append("_source_id", detail?.master?.sm_id);
            formData.append("_payee_id", target.id);
            formData.append("_surface", "suppliers_page");
         }
         const { responseData } = await fetchData(url, "POST", formData, "form-data");
         const result = responseData?.payload?.[0];
         if (responseData?.st === 1 && result?.status !== "error") {
            triggerToast("success",
               mode.type === "move"
                  ? `"${mode.alias.name_raw || mode.alias.name}" now resolves to ${target.name}.`
                  : `${detail?.master?.name} merged into ${target.name}. Its names, templates and history moved.`,
               false, mode.type === "move" ? "Alias moved" : "Supplier merged", false);
            await loadDetail();
            onChanged?.();
            if (mode.type === "merge") onClose?.();
         } else {
            triggerToast("warning", result?.reason || responseData?.mg || "Action failed.", false, "", false);
         }
      } catch {
         triggerToast("error", "We seem to have an issue accessing your data, please try again later.", true, "Network Issue", false, 0);
      } finally {
         setIsWorking(false);
         setPendingAction(null);
         setPickerMode(null);
      }
   };

   if (!supplier) return null;
   const master = detail?.master;
   const aliases = Array.isArray(detail?.aliases) ? detail.aliases : [];
   const templates = Array.isArray(detail?.templates) ? detail.templates : [];

   // Payee state, computed from the fresh detail rather than the (possibly stale) table row:
   // a user decision on any alias ratifies the supplier; sm_validated is the explicit mark.
   const derivedRatified = aliases.some((a) => a.source === "USER_CONFIRMED" || a.method === "manual_pick");
   const isPayee = master?.validated === true || derivedRatified;
   const canMark = master && !master.is_retired && !isPayee;
   // Removing the mark is only offered when the mark is what makes it a payee —
   // ratification earned through merges/relinks/onboarding is not casually undoable.
   const canUnmark = master?.validated === true && !derivedRatified;

   const masterAddress = addressLine(master);

   return (
      <>
         <Offcanvas show placement="end" onHide={onClose} className="supplier-detail-panel">
            <Offcanvas.Header closeButton>
               <div className="panel-head">
                  <Offcanvas.Title>{supplier.name}</Offcanvas.Title>
                  {master && (
                     <div className="panel-head-status">
                        {master.is_retired ? (
                           <span className="payee-badge retired" title="Merged into another supplier — its names and history moved there">Retired</span>
                        ) : isPayee ? (
                           <span className="payee-badge live" title={master.validated && !derivedRatified
                              ? "Marked as a payee by a user"
                              : "Confirmed as a payee by a user decision (merge, name move, or onboarding)"}>Payee</span>
                        ) : (
                           <span className="payee-badge pending" title="Imported from the ERP — no one has reviewed this supplier yet">Unreviewed</span>
                        )}
                        {canMark && (
                           <Button variant="outline-primary" size="sm" className="panel-payee-btn"
                                   disabled={isWorking} onClick={() => setPayeeMark(true)}>
                              Mark as payee
                           </Button>
                        )}
                        {canUnmark && (
                           <Button variant="link" size="sm" className="panel-payee-undo"
                                   disabled={isWorking} onClick={() => setPayeeMark(false)}>
                              Remove payee mark
                           </Button>
                        )}
                     </div>
                  )}
               </div>
            </Offcanvas.Header>
            <Offcanvas.Body>
               {isLoading ? (
                  <p className="text-secondary">Loading…</p>
               ) : !detail ? (
                  <p className="text-secondary">Could not load supplier detail.</p>
               ) : (
                  <>
                     <div className="panel-meta">
                        <span className="panel-meta-key">Address</span>
                        <span className="panel-meta-val">
                           {masterAddress ? (
                              <>
                                 {master?.street && <div>{master.street}</div>}
                                 <div>{[master?.city, master?.province, master?.postal_code].filter(Boolean).join(", ")}</div>
                                 {master?.country && <div>{master.country}</div>}
                              </>
                           ) : "—"}
                        </span>
                        <span className="panel-meta-key">Terms</span>
                        <span className="panel-meta-val">
                           {master?.terms_label || (master?.terms_net_days != null ? `Net ${master.terms_net_days}` : "—")}
                        </span>
                        <span className="panel-meta-key">Imported</span>
                        <span className="panel-meta-val">{fmtDate(master?.imported_at) || "—"}</span>
                        <span className="panel-meta-key">Templates</span>
                        <span className="panel-meta-val">
                           {templates.length === 0 ? "—" : templates.map((t) => (
                              <div key={`cfg-${t.config_id}`} className="small">
                                 #{t.config_id} {t.canonical_name} v{t.version}{t.active ? "" : " (inactive)"}
                              </div>
                           ))}
                        </span>
                     </div>

                     <div className="panel-stats">
                        <div className="panel-stat">
                           <div className="panel-stat-value">{Number(master?.erp_bill_count || 0).toLocaleString()}</div>
                           <div className="panel-stat-label">ERP invoices</div>
                           <div className="panel-stat-sub">
                              {master?.erp_last_date ? `last ${fmtDate(master.erp_last_date)}` : "none yet"}
                           </div>
                        </div>
                        <div className="panel-stat">
                           <div className="panel-stat-value">{Number(master?.statement_count || 0).toLocaleString()}</div>
                           <div className="panel-stat-label">Statements</div>
                           <div className="panel-stat-sub">
                              {master?.statement_last_date ? `last ${fmtDate(master.statement_last_date)}` : "none yet"}
                           </div>
                        </div>
                     </div>

                     <div className="panel-section-label">Known names ({aliases.length})</div>
                     {aliases.length === 0 ? (
                        <p className="text-secondary small">
                           No names point here — this supplier is retired. Nothing was deleted; its history moved with the merge.
                        </p>
                     ) : (
                        aliases.map((a) => {
                           const aliasAddr = addressLine(a);
                           const contactBits = [a.phone, a.email].filter(Boolean);
                           const modifiedBy = protractorUser(a.protractor_modified_by);
                           return (
                              <div key={`alias-${a.alias_id}`} className="alias-card">
                                 <div style={{ minWidth: 0 }}>
                                    <div className="alias-name">{a.name_raw || a.name}</div>
                                    <div className="alias-meta">
                                       <Badge bg={SOURCE_VARIANT[a.source] || "secondary"} className="me-1">{a.source || "?"}</Badge>
                                       {a.location_name ? `${a.location_name} · ` : ""}{a.created_at}
                                       {a.method === "manual_pick" ? " · user decision" : ""}
                                    </div>
                                    {aliasAddr && <div className="alias-detail">{aliasAddr}</div>}
                                    {contactBits.length > 0 && <div className="alias-detail">{contactBits.join(" · ")}</div>}
                                    {modifiedBy && (
                                       <div className="alias-detail alias-provenance">
                                          Updated in Protractor by {modifiedBy}
                                          {a.protractor_modified_at ? ` · ${fmtDate(a.protractor_modified_at)}` : ""}
                                       </div>
                                    )}
                                 </div>
                                 <Button variant="outline-secondary" size="sm"
                                         onClick={() => setPickerMode({ type: "move", alias: a })}>
                                    Move…
                                 </Button>
                              </div>
                           );
                        })
                     )}

                     {!master?.is_retired && (
                        <div className="panel-merge-row">
                           <div className="panel-section-label">Same payee as another supplier?</div>
                           <div className="panel-merge-help">
                              Merging retires <strong>{supplier.name}</strong> and moves its {aliases.length} name{aliases.length === 1 ? "" : "s"},
                              templates, statements and ERP history to the supplier you pick. Nothing is deleted.
                           </div>
                           <Button variant="outline-danger" size="sm"
                                   onClick={() => setPickerMode({ type: "merge" })}>
                              Merge into another supplier…
                           </Button>
                        </div>
                     )}
                  </>
               )}
            </Offcanvas.Body>
         </Offcanvas>

         <PayeePickerModal
            show={Boolean(pickerMode) && !pendingAction}
            onClose={() => setPickerMode(null)}
            onSelect={(record) => setPendingAction({ mode: pickerMode, target: record })}
            title={pickerMode?.type === "move"
               ? `Move "${pickerMode?.alias?.name_raw || pickerMode?.alias?.name}" to…`
               : `Merge ${supplier.name} into…`}
            helperText={pickerMode?.type === "merge"
               ? `Pick the supplier that should be kept — ${supplier.name} will retire into it.`
               : ""}
            initialQuery={pickerMode?.type === "move" ? (pickerMode?.alias?.name_raw || "") : (supplier?.name || "")}
            excludeMasterId={detail?.master?.sm_id}
         />

         {pendingAction && (
            <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.4)", zIndex: 1070 }}>
               <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content merge-chooser">
                     <div className="modal-body">
                        <p className="fw-semibold mb-1">
                           {pendingAction.mode.type === "move"
                              ? "Move this name?"
                              : `Merge into ${pendingAction.target.name}?`}
                        </p>
                        {pendingAction.mode.type === "move" ? (
                           <div className="merge-effect mt-2">
                              Future statements and ERP rows named <strong>"{pendingAction.mode.alias.name_raw || pendingAction.mode.alias.name}"</strong> will
                              resolve to <strong>{pendingAction.target.name}</strong>. This name's existing history moves with it.
                           </div>
                        ) : (
                           <div className="merge-effect mt-2">
                              <strong>{pendingAction.target.name}</strong> is kept and receives everything.{" "}
                              <strong>{supplier.name}</strong> hands over its {aliases.length} name{aliases.length === 1 ? "" : "s"},
                              templates, statements and ERP history, then retires. Nothing is deleted, and a name can be moved back later.
                           </div>
                        )}
                     </div>
                     <div className="modal-footer py-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPendingAction(null)} disabled={isWorking}>Cancel</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={runAction} disabled={isWorking}>
                           {isWorking ? "Working…" : pendingAction.mode.type === "move" ? "Move name" : `Merge into ${pendingAction.target.name}`}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}
      </>
   );
}
